import { Agent } from './agent';
import { ListingType, ListingTypeMap, ListPriceMove, SortBy } from '../classes/enums';
import { DetailStub } from '../components/listing-details/detail-section/detail-section.component';
import { IDemographicsResponse, Demographics } from './demographics';
import OpenHouse from './open-house';
import {Neighbourhood} from './neighbourhood';
import { environment } from '../../environments/environment-wrapper';

export class Listing {

  constructor(listingStub?: any, favouriteListings?: string[]) {
    if (listingStub) {
      Object.keys(listingStub).forEach((key) => {
        this[key] = listingStub[key];
      });

      this.fullAddress = [this.address, this.city, this.province].join(', ') + ' ' + this.postalCode;

      // Set constructor values here
      this.isHouse = ListingTypeMap.house.indexOf(this.listingTypeId) >= 0;
      this.isTownhouse = ListingTypeMap.townhouse.indexOf(this.listingTypeId) >= 0;
      this.isCondo = ListingTypeMap.condo.indexOf(this.listingTypeId) >= 0;
      this.isCommercial = ListingTypeMap.commercial.indexOf(this.listingTypeId) >= 0;
      this.isHouseTownhome = (ListingTypeMap.house.indexOf(this.listingTypeId) !== -1 || ListingTypeMap.townhouse.indexOf(this.listingTypeId) !== -1);
      const leaseOrRentalTypes = [ListingType.Rental, ListingType.CommercialLease, ListingType.Lease, ListingType.VacationRental];
      this.isLeaseOrRental = leaseOrRentalTypes.indexOf(this.listingTypeId) >= 0;

      if (this.baths) {
        this.totalBaths = this.bathsHalf ? `${this.baths} + ${this.bathsHalf}` : '' + this.baths;
      }
      this.totalBeds = this.beds ? '' + this.beds : null;
      this.id = this.listingId;

      if (favouriteListings && favouriteListings.length) {
        this.isFavourite = favouriteListings.indexOf(this.listingId) >= 0;
      }

      // This little hack is needed in order to call Agent's constructor to set flag
      if (this.agent) {
        this.agent = new Agent(this.agent);
      }
      if (this.coAgent) {
        this.coAgent = new Agent(this.coAgent);
      }

      // TODO: dont need this after they update their code to remove domains
      const officeProfileUrlRegex = /https:\/\/www\.remax\.ca/g;
      this.detailUrl = this.detailUrl ? '/' + this.detailUrl.replace(officeProfileUrlRegex, '') : '';


      this.getFavouriteIconToUse();
      this._setPlaceholderImage();
      this._normalizeIDXDisclosure();
      this._isFarm();

      this.jsonLdSchema = [{
        '@context': 'http://schema.org',
        '@graph': [
          {
            '@type': 'SingleFamilyResidence',
            address: {
              '@type': 'PostalAddress',
              streetAddress: this.fullAddress,
              addressLocality: this.city,
              addressRegion: this.province,
              postalCode: this.postalCode
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: this.lat,
              longitude: this.lng
            },
          },
          {
            '@type': 'House',
            numberOfRooms: this.rooms,
            address: {
              '@type': 'PostalAddress',
              streetAddress: this.fullAddress,
              addressLocality: this.city,
              addressRegion: this.province,
              postalCode: this.postalCode
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: this.lat,
              longitude: this.lng
            },
            description: this.description,
            photo: {
              '@type': 'ImageObject',
              url: this.imageUrls
            }
          },
          {
            '@type': 'Offer',
            price: this.listPrice,
            priceCurrency: 'CAD',
            url: this.detailUrl
          }
        ]
      }];

      if(this.hasOpenHouse){
        const openHouseSERPs = this.openHouses.map(function(house){
          return{
            '@type': 'Event',
            'description': 'Open House',
            'name': 'Open House - ' + house.timeDescription,
            'startDate': house.startTime,
            'location':{
              '@type': 'Place',
              'name': 'Open House',
              address: {
                '@type': 'PostalAddress',
                streetAddress: this.fullAddress,
                addressLocality: this.city,
                addressRegion: this.province,
                postalCode: this.postalCode
              }
            },
            'image': this.imageUrls[0],
            'endDate': house.endTime,
            'performer': {
              '@type': 'organization',
              'name': 'RE/MAX'
            },
            'offers': {
              '@type': 'Offer',
              'name' : 'Open House',
              'url' : environment.cdnServerUrl.slice(0, -1) + this.detailUrl,
              'price' : this.listPrice,
              'validFrom' : house.startTime,
              'validThrough' : house.endTime,
              'priceCurrency' : 'CAD',
              'availability' : 'LimitedAvailability'
            }}
        }, this);
        this.jsonLdSchema[0]['@graph'] = this.jsonLdSchema[0]['@graph'].concat(openHouseSERPs);
      }

      this.openGraphContent = {
        bathroom: this.baths,
        bedroom:  this.beds,
        area: this.sqFt,
        propertyType: ListingType[this.listingTypeId],
        propertyAddress: this.fullAddress
      };

    }
    if (this.communityName && this.city && this.communityName.toLowerCase() === this.city.toLowerCase()) {
      this.communityName = null;
    }
  }

  setDemographics(data: IDemographicsResponse) {
    this.demographics = new Demographics(data);
  }

  id: string; // Set to ListingId just to share a similar property to school

  listingId: string = '';
  isRemaxListing: boolean;
  isLuxury: boolean;
  acreage: string;
  active: boolean;
  address: string;
  agentIncorporation: string;
  airConditioning: boolean;
  area: string;
  age: string;
  basement: string;
  basementFinished: string;
  bath2: string;
  baths: number;
  bathsFull: string;
  bathsHalf: string;
  bed2Dim: string;
  bed3Dim: string;
  bed4Dim: string;
  bed5Dim: string;
  beds: number;
  boardName: string;
  bonusDim: string;
  breakfastDim: string;
  buildingName: string;
  city: string;
  communityName: string;
  contractConditions: string;
  cooling: boolean;
  country: string;
  county: string;
  denDim: string;
  description: string
  diningRoomDim: string;
  driveway: string;
  exterior: string;
  familyRoomDim: string;
  fees: number;
  fireplace: boolean;
  fireplaces: number;
  fireplaceType: string;
  formalDiningAreaDim: string;
  foundation: string;
  foyerDim: string;
  gameRoomDim: string;
  garage: boolean;
  garageSpaces: number;
  garageType: string;
  greatRoomDim: string;
  hasOpenHouse: boolean;
  heating: string;
  heatingSource: string;
  kitchenDim: string;
  lastUpdated: Date;
  lat: number;
  laundryDim: string;
  listingDate: Date;
  listingStatus: string;
  listingTypeId: number;
  listPrice: number = 0;
  listPriceMove: ListPriceMove;
  livingRoomDim: string;
  lng: number;
  lotSize: number;
  masterBath: string;
  masterBedDim: string;
  masterBedroomFirstFloor: boolean;
  mediaRoomDim: string;
  mlsNum: string;
  modelType: string;
  occupancy: string;
  officeDenDim: string;
  openHouses: OpenHouse[] = [];
  otherRoom1Dim: string;
  otherRoom2Dim: string;
  ownershipType: string;
  parkingSpaces: number;
  partialBathrooms: string;
  petPolicy: string;
  pool: boolean;
  postalCode: string;
  province: string;

  // TODO: this is so stupid
  remaxAgentId: string;
  remaxAgentName: string;
  remaxAgentSourceSystemId: string;
  remaxCoAgentId: string;
  remaxCoAgentName: string;
  remaxCoAgentSourceSystemId: string;

  remaxOfficeId: string;
  remaxOfficeName: string;

  roof: string;
  rooms: number;
  schoolDistrict: string;
  services: string;
  sewer: string;
  source: string; // Listing Source ID
  sqFt: number = 0;
  pricePerSqFt: number;
  stories: number;
  style: string;
  subDivision: string;
  taxAmount: number;
  taxYear: number;
  totalAcres: number;
  transactionType: string;
  utilityDim: string;
  videoUrl: string;
  virtualTourUrl: string;
  waterfront: boolean;
  waterview: boolean;

  agent: Agent;
  coAgent: Agent;
  imageUrls: Array<string>;
  features: { [key: string]: Array<string> };

  detailUrl: string;

  // Commercial Only
  primaryBusinessType?: string;

  // Front end specific
  hasBeenViewed: boolean = false;
  isFavourite: boolean = false;
  selected: boolean;

  // Missing
  headline: string; // Single Family in Wellesley

  // Custom properties for logic purposes
  isHouse: boolean = false;
  isTownhouse: boolean = false;
  isCondo: boolean = false;
  isCommercial: boolean = false;
  isHouseTownhome: boolean = false;
  isLeaseOrRental: boolean = false;
  totalBaths: string;
  totalBeds: string;
  fullAddress: string;
  idxDisclosure: string;
  isFarm: boolean;
  favouriteIconToUse: string; // Which favourite icon we want to use

  placeHolderImage: string; // Placeholder Image URL in case retrieving the listing image fails

  highLevelDetailRows: Array<DetailStub>;
  keyFactRows: Array<DetailStub>;
  insideRows: Array<DetailStub>;
  roomDetailRows: Array<DetailStub>;
  outsideRows: Array<DetailStub>;
  utilityRows: Array<DetailStub>;
  additionalFeatureRows: Array<DetailStub>;

  jsonLdSchema: object[] = [];
  demographics: Demographics;
  neighbourhood: Neighbourhood;

  openGraphContent: {
    bedroom: number,
    bathroom: number,
    area: number,
    propertyType: string,
    propertyAddress: string
  }; // For Ask Avenue - {“propertyAddress":"1455 Benzer Crescent,, Kelowna, BC V1V 2Y1”, "bathroom":"3.00”, "bedroom":"5”, "area":"3613”, "propertyType":"SingleFamily”}


  // off market listings based properties
  propertyId?: string;
  isBlacklisted?: boolean;
  isOffMarket?: boolean;

  static trackBy(index, listing: Listing) {
    return listing.listingId;
  }

  static sortBy(o1: Listing, o2: Listing, sortByValue: SortBy, isPricePerSqft: boolean = false) {
    switch (sortByValue) {
      case SortBy.OLDEST:
        if (o1.listingDate > o2.listingDate) return 1;
        else if (o1.listingDate < o2.listingDate) return -1;
        return 0;
      case SortBy.PRICE_HIGH_TO_LOW:
        if (isPricePerSqft) {
          if (o1.pricePerSqFt < o2.pricePerSqFt) return 1;
          else if (o1.pricePerSqFt > o2.pricePerSqFt) return -1;
          return 0;
        } else {
          if (o1.listPrice < o2.listPrice) return 1;
          else if (o1.listPrice > o2.listPrice) return -1;
          return 0;
        }
      case SortBy.PRICE_LOW_TO_HIGH:
        if (isPricePerSqft) {
          if (o1.pricePerSqFt > o2.pricePerSqFt) return 1;
          else if (o1.pricePerSqFt < o2.pricePerSqFt) return -1;
          return 0;
        } else {
          if (o1.listPrice > o2.listPrice) return 1;
          else if (o1.listPrice < o2.listPrice) return -1;
          return 0;
        }
      default:
        if (o1.listingDate < o2.listingDate) return 1;
        else if (o1.listingDate > o2.listingDate) return -1;
        return 0;
    }
  }

  /**
   * this is used on the favourites gallery where you may have listings that have price per sqft mixed
   * with listings that have regular price
   * @param {Listing} o1
   * @param {Listing} o2
   * @param {SortBy} sortByValue
   */
  static sortByMixed(o1: Listing, o2: Listing, sortByValue: SortBy) {
    const priceO1 = o1.pricePerSqFt ? o1.pricePerSqFt : o1.listPrice;
    const priceO2 = o2.pricePerSqFt ? o2.pricePerSqFt : o2.listPrice;
    switch (sortByValue) {
      case SortBy.OLDEST:
        if (o1.listingDate > o2.listingDate) return 1;
        else if (o1.listingDate < o2.listingDate) return -1;
        return 0;
      case SortBy.PRICE_HIGH_TO_LOW:
        if (priceO1 < priceO2) return 1;
        else if (priceO1 > priceO2) return -1;
        return 0;
      case SortBy.PRICE_LOW_TO_HIGH:
        if (priceO1 > priceO2) return 1;
        else if (priceO1 < priceO2) return -1;
        return 0;
      default:
        if (o1.listingDate < o2.listingDate) return 1;
        else if (o1.listingDate > o2.listingDate) return -1;
        return 0;
    }
  }

  getFavouriteIconToUse() {
    this.favouriteIconToUse = this.isFavourite
      ? (this.isCommercial || this.isLuxury ? '/assets/icons/heart/heart_icon_red_white@2x.svg' : '/assets/icons/heart/heart_icon_red@2x.svg')
      : (this.isCommercial || this.isLuxury ? '/assets/icons/heart/heart_icon_white@2x.svg' : '/assets/icons/heart/heart_icon@2x.svg');
  }

  /*
   * The content of idxDisclosure attribute contains html tags and some programming langugage related functions
   * The method removes html tags from string and replaces multiple spaces with single one also replaces @Year@ with current year
   * and @State@ with two character province name
   */
  private _normalizeIDXDisclosure() {
    if (this.idxDisclosure) {
      // To remove html tags,  Copyright and ©
      const htmlTagRegex = RegExp('<[^>]*>|$', 'g');
      // Replace string '@Year@' with value of current year
      const currentYearRemax = RegExp('@Year@', 'g');
      // To replace multiple spaces with a single one
      const singleSpaceRegex = RegExp('\\s{2,}', 'g');
      // To replace string '@State@' with actual province name
      const province = RegExp('@State@', 'g');

      this.idxDisclosure = this.idxDisclosure.replace(htmlTagRegex, '')
        .replace(singleSpaceRegex, ' ')
        .replace(currentYearRemax, String(new Date().getFullYear()))
        .replace(province, this.province)
        .trim();
    }
  }

  private _isFarm() {
    if (ListingType.Farm.valueOf() === this.listingTypeId) {
      this.isFarm = true;
      this.primaryBusinessType = 'Farm';
    } else {
      this.isFarm = false;
    }
  }

  private _setPlaceholderImage() {
    if (this.isCommercial) this.placeHolderImage = '/assets/listing-default-image/commercial_placeholder@1x.svg';
    else if (this.isLuxury) this.placeHolderImage = '/assets/listing-default-image/luxury_placeholder@1x.svg';
    else this.placeHolderImage = '/assets/listing-default-image/residential_placeholder@1x.svg';
  }
}


